/**
 * EventCreateModal — 일정 생성/수정 모달 (visibility 모델)
 *
 * - visibility: private / public_channels / public_workspace
 * - public_channels: 다중 채널 공개
 * - 초대(attendees): 워크스페이스 멤버 누구나
 * - 게스트: 채널 캘린더 컨텍스트(forceChannel)에서 "채널 일정" 만 생성/수정 — 개인·전사는 차단
 * - public_workspace: admin 전용 (forceWorkspace prop 으로 헤더 별도 버튼에서 진입)
 *
 * 합의: .agent/plans/calendar_phase1_design.md + 2026-05-21 추가 합의
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../components/ui/use-toast';
import { useAppUIContext } from '../../context/AppUIContext';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useCalendarContext } from '../../context/CalendarContext';
import { My } from '../../services/My';
import {
  CalendarEvent, EventVisibility,
  canManageEvent,
} from '../../models/CalendarEvent';
import {
  createEvent, updateEvent, inviteAttendees, fetchAttendees, setMyReminders, fetchMyReminders,
} from '../../services/CalendarService';
import { Fire } from '../../services/FireClient';
import { Hash, X, ChevronDown, Repeat, Bell, CalendarIcon, Clock } from 'lucide-react';
import { AdminBadge } from '../components/AdminBadge';
import { cn } from '../../lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { UserAvatar } from '../components/UserAvatar';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  RecurrencePreset, recurrencePresetLabel, presetToRRule, describeRRule,
} from './recurrence';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 수정 모드 */
  event?: CalendarEvent | null;
  /** 생성 모드의 초기 시작일 */
  initialDate?: Date | null;
  /** 전사 일정 강제 (admin 별도 버튼 진입) */
  forceWorkspace?: boolean;
  /** 채널 일정 강제 (채널 캘린더 안에서 [+ 새 일정] 진입 시) */
  forceChannel?: { channelId: string; channelName: string };
}

interface MemberLite { id: string; name: string; email?: string; photoUrl?: string | null; }

function toInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromInputValue(v: string): string {
  return new Date(v).toISOString();
}

function defaultTimes(anchor: Date): { start: Date; end: Date } {
  const start = new Date(anchor);
  start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30, 0, 0);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);
  return { start, end };
}

/** "YYYY-MM-DDTHH:MM" 문자열 → 날짜 부분만 Date(local midnight) 추출 */
function parseDatePart(input: string): Date | undefined {
  if (!input) return undefined;
  const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "YYYY-MM-DDTHH:MM" → "HH:MM" 시간 부분 */
function parseTimePart(input: string): string {
  const m = input.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : '00:00';
}

/** Date + "HH:MM" → "YYYY-MM-DDTHH:MM" datetime-local 형식 */
function joinDateAndTime(date: Date, time: string): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const safeTime = /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${safeTime}`;
}

/** 사용자가 읽기 쉬운 날짜 라벨 — "2026년 5월 22일 (금)" */
function formatDateLabel(input: string): string {
  const d = parseDatePart(input);
  if (!d) return t`Select date`;
  return d.toLocaleDateString(i18n.locale || 'en', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  });
}

const EventCreateModal: React.FC<Props> = ({
  open, onOpenChange, event, initialDate, forceWorkspace, forceChannel,
}) => {
  const isEdit = !!event;
  const { activeWorkspaceId, rooms } = useAppUIContext();
  const myRole = useMyRole();
  const { refreshEvents } = useCalendarContext();

  // 본인이 속한 일반 채널만 (1:1/개인룸/direct 모두 제외)
  const myChannels = useMemo(() => rooms.filter((r) =>
    r.workspaceId === activeWorkspaceId
    && (r.visibility === 'listed' || r.visibility === 'unlisted')
    && !r.isOneToOne
  ), [rooms, activeWorkspaceId]);

  const isGuest = myRole === 'guest';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [sharedChannelIds, setSharedChannelIds] = useState<string[]>([]);
  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [allMembers, setAllMembers] = useState<MemberLite[]>([]);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [recurrencePreset, setRecurrencePreset] = useState<RecurrencePreset>('none');
  /** 수정 모드에서 원본 RRULE 이 프리셋과 맞지 않으면 'custom' 으로 유지 */
  const [customRRule, setCustomRRule] = useState<string | null>(null);
  const [reminders, setReminders] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // 위치/설명/채널공개/참석자 = 옵션 영역 (기본 접힘). 수정 모드에서 값 있으면 자동 펼침은 아래 effect 에서.
  const [showMore, setShowMore] = useState(false);

  // 반복 일정은 채널 공개 금지 (알림 노이즈 방지). 초대는 가능.
  const isRecurring = recurrencePreset !== 'none';

  // 반복 켜지면 선택된 채널 자동 해제
  useEffect(() => {
    if (isRecurring && sharedChannelIds.length > 0) {
      setSharedChannelIds([]);
    }
  }, [isRecurring]);

  // visibility 자동 derive
  // - forceWorkspace → public_workspace
  // - forceChannel → public_channels (owner_channel_id 가 강제됨)
  // - 그 외: 채널 공유 있으면 public_channels, 없으면 private
  const visibility: EventVisibility = useMemo(() => {
    if (forceWorkspace) return 'public_workspace';
    if (forceChannel) return 'public_channels';
    if (sharedChannelIds.length > 0) return 'public_channels';
    return 'private';
  }, [forceWorkspace, forceChannel, sharedChannelIds]);

  // 리마인더 = per-user 개인 알림.
  // 전사 일정 / 채널 소유 일정은 작성 모달에서 리마인더 항목 숨김 (상세에서 각자 설정).
  // 개인 일정 (채널에 공개한 개인 일정 포함) 은 작성 시 본인 리마인더 설정 가능.
  const isChannelOwned = !!forceChannel || (isEdit && !!event?.owner_channel_id);
  const isWorkspaceEvent = forceWorkspace || (isEdit && event?.visibility === 'public_workspace');
  const showReminderField = !isChannelOwned && !isWorkspaceEvent;

  // 게스트는 "채널 일정"(forceChannel 진입 또는 채널 소유 일정 수정)만 다룰 수 있음.
  // 개인(메인 캘린더)·전사 일정 생성은 게스트 계속 차단 (설계: 게스트는 채널만).
  const guestBlocked = isGuest && !isChannelOwned;

  // 채널 모드에선 다른 채널 공유 시 본인 채널만 노출 (강제 채널 제외)
  const otherChannels = useMemo(() => {
    if (!forceChannel) return myChannels;
    return myChannels.filter((r) => r.id !== forceChannel.channelId);
  }, [myChannels, forceChannel]);

  // 워크스페이스 멤버 로드 (초대용)
  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    Fire.ws.getWorkspaceMembers(activeWorkspaceId).then((members) => {
      setAllMembers((members ?? [])
        .filter((m) => m && m.id)
        .map((m) => ({
          id: m.id,
          name: m.user?.name ?? m.user?.email ?? t`(No name)`,
          email: m.user?.email ?? '',
          photoUrl: m.user?.photoUrl ?? null,
        })));
    }).catch(() => {});
  }, [open, activeWorkspaceId]);

  // 모달 열림/초기값
  useEffect(() => {
    if (!open) return;

    if (isEdit && event) {
      setTitle(event.title);
      setDescription(event.description ?? '');
      setLocation(event.location ?? '');
      setSharedChannelIds(event.shared_channel_ids ?? []);
      // 옵션값(위치/설명/공유채널)이 있으면 옵션 영역 펼친 채로 시작
      setShowMore(!!event.location || !!event.description || (event.shared_channel_ids?.length ?? 0) > 0);
      setStartInput(toInputValue(new Date(event.start_at)));
      setEndInput(toInputValue(new Date(event.end_at)));
      setIsAllDay(event.is_all_day);
      // 수정 모드: 기존 RRULE 이 프리셋 중 하나면 그걸 선택, 아니면 custom
      if (event.recurrence_rule) {
        const presets: RecurrencePreset[] = ['daily', 'weekdays', 'weekly', 'monthly_date', 'monthly_nth_weekday', 'yearly'];
        const startD = new Date(event.start_at);
        const matched = presets.find((p) => presetToRRule(p, startD) === event.recurrence_rule);
        if (matched) {
          setRecurrencePreset(matched);
          setCustomRRule(null);
        } else {
          setRecurrencePreset('custom');
          setCustomRRule(event.recurrence_rule);
        }
      } else {
        setRecurrencePreset('none');
        setCustomRRule(null);
      }
      // 내 per-user 리마인더 로드 (events.reminders deprecated)
      if (My.user.id) {
        fetchMyReminders(event.id, My.user.id)
          .then((mins) => setReminders(mins))
          .catch(() => setReminders([]));
      } else {
        setReminders([]);
      } // eslint-disable-line no-else-return
      // 기존 attendees 로드
      fetchAttendees(event.id).then((list) => {
        setInvitedUserIds(list.filter((a) => a.user_id !== event.owner_id).map((a) => a.user_id));
      }).catch(() => setInvitedUserIds([]));
    } else {
      const anchor = initialDate ?? new Date();
      const { start, end } = defaultTimes(anchor);
      setTitle('');
      setDescription('');
      setLocation('');
      setSharedChannelIds([]);
      setInvitedUserIds([]);
      setStartInput(toInputValue(start));
      setEndInput(toInputValue(end));
      setIsAllDay(false);
      setRecurrencePreset('none');
      setCustomRRule(null);
      setReminders([]); // default: 없음 (per-user 모델 — 작성자가 원할 때만)
      setShowMore(false); // 생성 모드: 옵션 영역 접힘으로 시작
    }
    setMemberQuery('');
  }, [open, isEdit, event?.id, initialDate?.getTime(), forceWorkspace]);

  const memberSuggestions = useMemo(() => {
    // 멘션 습관으로 "@kim" 처럼 입력하는 케이스 지원 — 앞의 @ 는 검색어에서 제외
    const q = memberQuery.trim().replace(/^@+/, '').toLowerCase();
    if (!q) return [];
    return allMembers
      .filter((m) => m.id !== My.user.id && !invitedUserIds.includes(m.id))
      .filter((m) =>
        (m.name ?? '').toLowerCase().includes(q)
        || (m.email ?? '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [memberQuery, allMembers, invitedUserIds]);

  const invitedMembers = useMemo(
    () => invitedUserIds.map((uid) => allMembers.find((m) => m.id === uid)).filter(Boolean) as MemberLite[],
    [invitedUserIds, allMembers],
  );

  const canSubmit = useMemo(() => {
    if (submitting || guestBlocked) return false;
    if (!title.trim() || !startInput || !endInput) return false;
    if (new Date(endInput) <= new Date(startInput)) return false;
    return true;
  }, [title, startInput, endInput, submitting, guestBlocked]);

  const toggleChannel = (id: string, checked: boolean) => {
    setSharedChannelIds((prev) =>
      checked ? [...prev.filter((c) => c !== id), id] : prev.filter((c) => c !== id),
    );
  };

  const removeChannel = (id: string) => {
    setSharedChannelIds((prev) => prev.filter((cid) => cid !== id));
  };

  const selectedChannels = useMemo(
    () => sharedChannelIds
      .map((cid) => myChannels.find((r) => r.id === cid))
      .filter(Boolean) as typeof myChannels,
    [sharedChannelIds, myChannels],
  );

  const canEditCurrent = useMemo(() => {
    if (!isEdit || !event) return true;
    return canManageEvent(event, myRole ?? 'guest', My.user.id);
  }, [isEdit, event?.id, myRole]);

  const addInvite = (uid: string) => {
    if (!invitedUserIds.includes(uid)) setInvitedUserIds([...invitedUserIds, uid]);
    setMemberQuery('');
  };

  const removeInvite = (uid: string) => {
    setInvitedUserIds(invitedUserIds.filter((id) => id !== uid));
  };

  // 시작 시간 변경 시 종료도 같은 길이(duration)를 유지하며 자동 이동 (Google Calendar 방식).
  // 종료를 직접 바꿀 때는 시작을 건드리지 않는다.
  const handleStartChange = (next: string) => {
    const prevStartMs = new Date(startInput).getTime();
    const endMs = new Date(endInput).getTime();
    setStartInput(next);
    const nextStartMs = new Date(next).getTime();
    if (Number.isNaN(prevStartMs) || Number.isNaN(endMs) || Number.isNaN(nextStartMs)) return;
    const duration = endMs - prevStartMs;
    if (duration <= 0) return; // 기존이 비정상(종료 ≤ 시작)이면 자동 보정하지 않음
    setEndInput(toInputValue(new Date(nextStartMs + duration)));
  };

  const handleSubmit = async () => {
    if (!activeWorkspaceId || !canSubmit) return;
    try {
      setSubmitting(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const startIso = fromInputValue(startInput);
      const endIso = fromInputValue(endInput);

      // RRULE 계산
      const startD = new Date(startInput);
      const recurrence_rule = recurrencePreset === 'custom'
        ? customRRule
        : presetToRRule(recurrencePreset, startD);

      let savedEventId: string;
      // owner_channel_id 결정
      // - 수정 모드: 기존 값 유지 (변경 불가 — 사용자 결정 #1)
      // - 생성 모드: forceChannel 이면 그 채널, 아니면 null
      const owner_channel_id = isEdit && event
        ? event.owner_channel_id
        : (forceChannel?.channelId ?? null);

      if (isEdit && event) {
        await updateEvent(event.id, {
          title: title.trim(),
          description: description.trim() || null,
          location: location.trim() || null,
          start_at: startIso,
          end_at: endIso,
          is_all_day: isAllDay,
          timezone,
          visibility,
          owner_channel_id,
          shared_channel_ids: visibility === 'public_channels' ? sharedChannelIds : [],
          recurrence_rule,
        });
        savedEventId = event.id;
        toast({ description: t`Event updated` });
      } else {
        const created = await createEvent({
          workspace_id: activeWorkspaceId,
          owner_id: My.user.id,
          creator: { uid: My.user.id, name: My.user.name, photoUrl: My.user.photoUrl ?? null },
          visibility,
          owner_channel_id,
          shared_channel_ids: sharedChannelIds,
          title: title.trim(),
          description: description.trim() || null,
          location: location.trim() || null,
          start_at: startIso,
          end_at: endIso,
          is_all_day: isAllDay,
          timezone,
          recurrence_rule,
        });
        savedEventId = created.id;
        toast({ description: t`Event created` });
      }

      // 리마인더 = per-user. 개인 일정(showReminderField)일 때만 작성자 본인 행 저장.
      // 전사/채널 일정은 작성 모달에 리마인더 UI 가 없으므로 여기서 건드리지 않음
      // (각 사용자가 상세 화면에서 개별 설정).
      // 신규 생성 시 리마인더가 0개면 reconcile(콜드스타트 callable)이 불필요 → setMyReminders 자체를 skip.
      //   (생성 화면에 리마인더 유무가 그대로 보이므로 클라이언트가 판단 가능 — 서버 왕복 없이 빠르게 저장)
      // 수정 모드는 기존 리마인더를 0개로 바꾼 '제거'도 반영해야 하니 항상 동기화.
      if (showReminderField && My.user.id && (isEdit || reminders.length > 0)) {
        await setMyReminders(savedEventId, My.user.id, reminders);
      }

      // 초대 처리 (생성 + 수정 모두). 본인은 organizer 로 자동 표시.
      // 전사 공개 일정은 RSVP 개념을 갖지 않음 — invite UI 자체가 비표시 → 호출 skip.
      if (visibility !== 'public_workspace' && invitedUserIds.length > 0) {
        await inviteAttendees(savedEventId, [My.user.id, ...invitedUserIds], My.user.id);
      }

      await refreshEvents();
      onOpenChange(false);
    } catch (err: any) {
      console.error('[EventCreateModal] Save failed:', err);
      toast({ description: err?.message ?? t`Failed to save event`, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (guestBlocked && !isEdit) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Trans>Event creation not allowed</Trans></DialogTitle></DialogHeader>
          <p className="text-caption text-muted-foreground"><Trans>Guests cannot create events. You can view and respond to invited events.</Trans></p>
          <DialogFooter><Button onClick={() => onOpenChange(false)} variant="outline" size="sm"><Trans>Close</Trans></Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (isEdit && !canEditCurrent) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle><Trans>Edit permission denied</Trans></DialogTitle></DialogHeader>
          <p className="text-caption text-muted-foreground"><Trans>You don't have permission to edit this event.</Trans></p>
          <DialogFooter><Button onClick={() => onOpenChange(false)} variant="outline" size="sm"><Trans>Close</Trans></Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(() => {
              if (isEdit) {
                if (event?.owner_channel_id) {
                  const channelName = myChannels.find((r) => r.id === event.owner_channel_id)?.name ?? t`(Channel)`;
                  return (<><Hash className="size-4 text-primary" /> #{channelName} <Trans>— Edit event</Trans></>);
                }
                if (event?.visibility === 'public_workspace') {
                  return (<>🌐 <Trans>Edit workspace event</Trans></>);
                }
                return (<>🔒 <Trans>Edit personal event</Trans></>);
              }
              if (forceWorkspace) return (<>🌐 <Trans>New workspace event</Trans></>);
              if (forceChannel) return (<><Hash className="size-4 text-primary" /> #{forceChannel.channelName} <Trans>— New event</Trans></>);
              return (<>🔒 <Trans>New personal event</Trans></>);
            })()}
            {/* 전사 일정은 관리자(owner/admin) 전용 — 게시판 화면과 동일 AdminBadge */}
            {isWorkspaceEvent && <AdminBadge className="shrink-0" />}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* 제목 */}
          <div className="flex flex-col gap-1">
            <label className="text-label font-medium text-foreground"><Trans>Title *</Trans></label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t`Event title`} autoFocus />
          </div>

          {/* 종일 토글 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={isAllDay} onCheckedChange={(v) => setIsAllDay(!!v)} />
            <span className="text-label text-foreground"><Trans>All-day event</Trans></span>
          </label>

          {/* 시작/종료 — 항상 두 줄 (한 row = 한 입력). 모바일·데스크탑 통일 */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-label font-medium text-foreground"><Trans>Start *</Trans></label>
              <DateTimeField
                value={startInput}
                onChange={handleStartChange}
                isAllDay={isAllDay}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-label font-medium text-foreground"><Trans>End *</Trans></label>
              <DateTimeField
                value={endInput}
                onChange={setEndInput}
                isAllDay={isAllDay}
                allDayDefaultTime="23:59"
              />
            </div>
          </div>

          {/* 반복 일정 */}
          <div className="flex flex-col gap-1">
            <label className="text-label font-medium text-foreground flex items-center gap-1.5">
              <Repeat className="size-3.5 text-muted-foreground" />
              <Trans>Repeat</Trans>
            </label>
            {(() => {
              const startD = startInput ? new Date(startInput) : new Date();
              const presets: RecurrencePreset[] = [
                'none', 'daily', 'weekdays', 'weekly',
                'monthly_date', 'monthly_nth_weekday', 'yearly',
              ];
              return (
                <Select
                  value={recurrencePreset}
                  onValueChange={(v) => setRecurrencePreset(v as RecurrencePreset)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {presets.map((p) => (
                      <SelectItem key={p} value={p}>
                        {recurrencePresetLabel(p, startD)}
                      </SelectItem>
                    ))}
                    {recurrencePreset === 'custom' && (
                      <SelectItem value="custom" disabled>
                        <Trans>Custom</Trans>: {describeRRule(customRRule, startD)}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              );
            })()}
          </div>

          {/* 리마인더 — per-user 개인 알림. 개인 일정 작성 시에만 노출 */}
          {showReminderField && (
            <div className="flex flex-col gap-1">
              <label className="text-label font-medium text-foreground flex items-center gap-1.5">
                <Bell className="size-3.5 text-muted-foreground" />
                <Trans>My personal reminder</Trans>
              </label>
              <Select
                value={reminders.length === 0 ? 'none' : String(reminders[0])}
                onValueChange={(v) => setReminders(v === 'none' ? [] : [parseInt(v, 10)])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none"><Trans>None</Trans></SelectItem>
                  <SelectItem value="0"><Trans>At start time</Trans></SelectItem>
                  <SelectItem value="5"><Trans>5 minutes before</Trans></SelectItem>
                  <SelectItem value="10"><Trans>10 minutes before</Trans></SelectItem>
                  <SelectItem value="15"><Trans>15 minutes before</Trans></SelectItem>
                  <SelectItem value="30"><Trans>30 minutes before</Trans></SelectItem>
                  <SelectItem value="60"><Trans>1 hour before</Trans></SelectItem>
                  <SelectItem value="120"><Trans>2 hours before</Trans></SelectItem>
                  <SelectItem value="1440"><Trans>1 day before</Trans></SelectItem>
                  <SelectItem value="2880"><Trans>2 days before</Trans></SelectItem>
                  <SelectItem value="10080"><Trans>1 week before</Trans></SelectItem>
                </SelectContent>
              </Select>
              <p className="text-meta text-muted-foreground">
                <Trans>Browser notification for you only. Works only while the app is open (mobile push in Phase 2).</Trans>
              </p>
            </div>
          )}

          {/* 옵션 더보기 토글 — 위치/설명/채널 공개/참석자를 접어둠 */}
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="flex items-center gap-1.5 text-label font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer py-1"
          >
            <ChevronDown className={cn('size-4 transition-transform duration-200', showMore && 'rotate-180')} />
            {showMore ? <Trans>Fewer options</Trans> : <Trans>More options (location, description, share, attendees)</Trans>}
          </button>

          {showMore && (<>
          {/* 위치 */}
          <div className="flex flex-col gap-1">
            <label className="text-label font-medium text-foreground"><Trans>Location</Trans></label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t`Meeting room, address (optional)`} />
          </div>

          {/* 설명 */}
          <div className="flex flex-col gap-1">
            <label className="text-label font-medium text-foreground"><Trans>Description</Trans></label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t`Detailed description (optional)`} rows={3} />
          </div>

          {/* 공개 채널 멘션 */}
          {forceWorkspace ? (
            <div className="flex items-center gap-2 pt-2 border-t border-border text-caption text-muted-foreground">
              <Hash className="size-4" />
              <Trans>Visible to all workspace members (workspace event)</Trans>
            </div>
          ) : forceChannel ? (
            <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
              <div className="text-caption text-muted-foreground flex items-center gap-1.5">
                <Hash className="size-3.5 text-primary" />
                <span className="text-foreground font-medium">#{forceChannel.channelName}</span>
                <span><Trans>— visible to all channel members</Trans></span>
              </div>

              {/* 다른 채널에 추가 공유 (옵션, 본인이 속한 다른 채널만) */}
              {otherChannels.length > 0 && (
                <>
                  <label className="text-label font-medium text-foreground mt-2"><Trans>Share to other channels (optional)</Trans></label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild disabled={isRecurring}>
                      <button
                        type="button"
                        disabled={isRecurring}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-input bg-background hover:bg-muted/40 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
                      >
                        <span className={selectedChannels.length === 0 ? 'text-muted-foreground text-body' : 'text-foreground text-body'}>
                          {isRecurring
                            ? t`Recurring events can't be shared with other channels`
                            : selectedChannels.length === 0
                              ? t`Select other channels (none by default)`
                              : `${selectedChannels.length} channels selected`}
                        </span>
                        <ChevronDown className="size-4 text-muted-foreground shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto" align="start">
                      <DropdownMenuLabel className="text-meta text-muted-foreground"><Trans>Other channels you belong to</Trans></DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {otherChannels.map((r) => (
                        <DropdownMenuCheckboxItem
                          key={r.id}
                          checked={sharedChannelIds.includes(r.id)}
                          onCheckedChange={(checked) => toggleChannel(r.id, !!checked)}
                          onSelect={(e) => e.preventDefault()}
                        >
                          <Hash className="size-3.5 mr-1.5 text-muted-foreground" />
                          {r.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {selectedChannels.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedChannels.map((ch) => (
                        <span
                          key={ch.id}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-meta"
                        >
                          <Hash className="size-3" />
                          {ch.name}
                          <button type="button" onClick={() => removeChannel(ch.id)} className="ml-1 hover:text-foreground">
                            <X className="size-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 pt-2 border-t border-border">
              <label className="text-label font-medium text-foreground"><Trans>Share with channel members</Trans></label>
              <p className="text-meta text-muted-foreground -mt-1">
                <Trans>Choose channels to announce a personal event (vacation, business trip, etc.). You remain the owner, and it won't appear in the channel calendar.</Trans>
              </p>

              {/* dropdown 멀티 선택 — 반복 일정이면 비활성화 */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isRecurring}>
                  <button
                    type="button"
                    disabled={isRecurring}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-input bg-background hover:bg-muted/40 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background"
                  >
                    <span className={selectedChannels.length === 0 ? 'text-muted-foreground text-body' : 'text-foreground text-body'}>
                      {isRecurring
                        ? t`Recurring events can't be shared with channel members`
                        : selectedChannels.length === 0
                          ? t`Select channels to share (private if none)`
                          : `${selectedChannels.length} channels shared`}
                    </span>
                    <ChevronDown className="size-4 text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto" align="start">
                  {myChannels.length === 0 ? (
                    <div className="px-3 py-2 text-caption text-muted-foreground"><Trans>You don't belong to any channels</Trans></div>
                  ) : (
                    <>
                      <DropdownMenuLabel className="text-meta text-muted-foreground">
                        <Trans>Channels you belong to</Trans>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {myChannels.map((r) => (
                        <DropdownMenuCheckboxItem
                          key={r.id}
                          checked={sharedChannelIds.includes(r.id)}
                          onCheckedChange={(checked) => toggleChannel(r.id, !!checked)}
                          onSelect={(e) => e.preventDefault()}
                        >
                          <Hash className="size-3.5 mr-1.5 text-muted-foreground" />
                          {r.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 선택된 채널 chips (요약) */}
              {selectedChannels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedChannels.map((ch) => (
                    <span
                      key={ch.id}
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-meta"
                    >
                      <Hash className="size-3" />
                      {ch.name}
                      <button
                        type="button"
                        onClick={() => removeChannel(ch.id)}
                        className="ml-1 hover:text-foreground"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <p className="text-meta text-muted-foreground">
                {isRecurring
                  ? t`Recurring events can't be shared with channel members (invites still work). Saved as private.`
                  : sharedChannelIds.length === 0
                    ? t`Saved as private (only you)`
                    : t`🔒 Saved as a personal event; members of the ${sharedChannelIds.length} selected channel(s) can see it in their main calendar.`}
              </p>
            </div>
          )}

          {/* 초대 (참석자) — 전사 공개 일정에선 비표시 (공지 성격, RSVP 가 어색) */}
          {visibility !== 'public_workspace' && (
          <div className="flex flex-col gap-2 pt-2 border-t border-border">
            <label className="text-label font-medium text-foreground"><Trans>Invite attendees</Trans></label>
            {invitedMembers.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {invitedMembers.map((m) => (
                  <span
                    key={m.id}
                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-body"
                  >
                    <UserAvatar src={m.photoUrl ?? undefined} name={m.name} className="size-5 shrink-0" />
                    {m.name}
                    <button
                      type="button"
                      onClick={() => removeInvite(m.id)}
                      className="ml-1 text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <Input
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
              placeholder={t`Search members by name...`}
            />
            {memberSuggestions.length > 0 && (
              <ul className="border border-border rounded-md max-h-40 overflow-y-auto">
                {memberSuggestions.map((m) => (
                  <li
                    key={m.id}
                    onClick={() => addInvite(m.id)}
                    className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted/50"
                  >
                    <UserAvatar src={m.photoUrl ?? undefined} name={m.name} className="size-6 shrink-0" />
                    <span className="text-body text-foreground">{m.name}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-meta text-muted-foreground">
              <Trans>Invited members receive a notification and can respond accept/decline/tentative.</Trans>
            </p>
          </div>
          )}
          </>)}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={submitting}><Trans>Cancel</Trans></Button>
          <Button size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? <Trans>Saving...</Trans> : isEdit ? <Trans>Edit</Trans> : <Trans>Create</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// 15분 간격 시간 목록 (00:00 ~ 23:45)
const TIME_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

/** "1130", "11:3", "9:5" 등 자유 입력 → "HH:MM"(유효) 또는 null */
function normalizeTimeInput(s: string): string | null {
  const m = s.trim().replace(/[^0-9:]/g, '').match(/^(\d{1,2}):?(\d{1,2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** 커스텀 시간 선택기 — Popover + 직접 입력(HH:MM, 임의 분) + 15분 간격 목록 */
const TimeField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const listRef = useRef<HTMLDivElement>(null);

  // 직접 입력한 임의 시간(15분 격자에 없는 값)도 목록에 끼워넣어 선택 표시 + 스크롤되게 한다.
  const options = useMemo(() => {
    if (!value || TIME_OPTIONS.includes(value)) return TIME_OPTIONS;
    return [...TIME_OPTIONS, value].sort();
  }, [value]);

  useEffect(() => {
    if (!open) return;
    setDraft(value);
    // 열릴 때 현재 값 항목을 보이게 스크롤
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector('[data-active="true"]') as HTMLElement | null;
      el?.scrollIntoView({ block: 'center' });
    });
  }, [open, value]);

  const commitDraft = () => {
    const norm = normalizeTimeInput(draft);
    if (norm) { onChange(norm); setDraft(norm); }
    else setDraft(value);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 h-9 px-2.5 rounded-md border border-input bg-background text-label text-foreground tabular-nums hover:bg-muted/50 transition-colors cursor-pointer shrink-0"
        >
          <Clock className="size-3.5 text-muted-foreground shrink-0" />
          <span>{value || '--:--'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-28 p-1.5" align="start">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDraft(); setOpen(false); } }}
          placeholder="HH:MM"
          inputMode="numeric"
          className="h-8 mb-1.5 text-center tabular-nums"
        />
        <div ref={listRef} className="max-h-48 overflow-y-auto flex flex-col gap-0.5 -mr-1 pr-1">
          {options.map((tOpt) => (
            <button
              key={tOpt}
              type="button"
              data-active={tOpt === value}
              onClick={() => { onChange(tOpt); setOpen(false); }}
              className={cn(
                'h-7 rounded text-label tabular-nums text-center cursor-pointer transition-colors',
                tOpt === value ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted/60 text-foreground',
              )}
            >
              {tOpt}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** 날짜+시간 합성 입력 — Popover Calendar (날짜) + 커스텀 TimeField (시간) */
interface DateTimeFieldProps {
  value: string;             // "YYYY-MM-DDTHH:MM"
  onChange: (v: string) => void;
  isAllDay: boolean;
  allDayDefaultTime?: string; // 종일 토글 off 시 기본 시간 (시작=현재 입력, 종료=23:59 기본)
}

const DateTimeField: React.FC<DateTimeFieldProps> = ({ value, onChange, isAllDay, allDayDefaultTime }) => {
  const [open, setOpen] = useState(false);
  const datePart = parseDatePart(value);
  const timePart = parseTimePart(value);
  const dateLabel = formatDateLabel(value);

  const handleDateSelect = (d: Date | undefined) => {
    if (!d) return;
    onChange(joinDateAndTime(d, isAllDay ? (allDayDefaultTime ?? '00:00') : timePart));
    setOpen(false);
  };

  return (
    <div className="flex items-stretch gap-1">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex-1 flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-label text-foreground hover:bg-muted/50 transition-colors cursor-pointer text-left"
          >
            <CalendarIcon className="size-4 text-muted-foreground shrink-0" />
            <span className="truncate">{dateLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={datePart}
            onSelect={handleDateSelect}
            autoFocus
          />
        </PopoverContent>
      </Popover>
      {!isAllDay && (
        <TimeField
          value={timePart}
          onChange={(tm) => datePart && onChange(joinDateAndTime(datePart, tm))}
        />
      )}
    </div>
  );
};

export default EventCreateModal;
